import { Checkbox, type CheckboxProps } from 'antd';
import STTypo from 'core/shared/typo';
import {
  ALL_FILTER_VALUE,
  type FilterOption,
  type FilterValue,
  type RegionalOverviewFilterType
} from '@features/regional-overview/regional-overview.models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSelectedText } from '../../filter.utils';
import FilterSection from '../filter-section/filter-section.component';
import { Container, VerticalCheckboxGroup } from './multi-select-filter.styles';

interface MultiSelectFilterProps<T extends RegionalOverviewFilterType> {
  filter: T;
  title: string;
  options: FilterOption[];
  allLabelKey?: string;
  translatable?: boolean;
  value: string[] | undefined;
  onFilterChanged: (filter: T, value: FilterValue) => void;
  defaultExpanded?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  dataTestID?: string;
}

const resolveCheckedList = (value: string[] | undefined, options: FilterOption[]): string[] => {
  if (!value) return [];
  if (value[0] === ALL_FILTER_VALUE) return options.map(o => o.value);
  return value;
};

const MultiSelectFilter = <T extends RegionalOverviewFilterType>({
  filter,
  title,
  options,
  allLabelKey,
  translatable,
  value,
  onFilterChanged,
  defaultExpanded,
  disabled,
  disabledTooltip,
  dataTestID
}: MultiSelectFilterProps<T>) => {
  const [t] = useTranslation();

  const checkedList = useMemo(() => resolveCheckedList(value, options), [value, options]);
  const isAllChecked = options.length > 0 && checkedList.length === options.length;
  const isIndeterminate = checkedList.length > 0 && checkedList.length < options.length;
  const selectedText = getSelectedText(t, isAllChecked, checkedList.length);

  const handleGroupChange = (list: string[]) => {
    const next = options.length > 0 && list.length === options.length ? [ALL_FILTER_VALUE] : list;
    onFilterChanged(filter, next);
  };

  const handleAllChange: CheckboxProps['onChange'] = e => {
    onFilterChanged(filter, e.target.checked ? [ALL_FILTER_VALUE] : []);
  };

  const handleClear = () => onFilterChanged(filter, []);

  return (
    <FilterSection
      name={title ?? ''}
      onClear={handleClear}
      selectedText={selectedText}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      defaultExpanded={defaultExpanded}
      dataTestId={dataTestID}>
      <Container>
        {allLabelKey !== undefined && (
          <Checkbox indeterminate={isIndeterminate} onChange={handleAllChange} checked={isAllChecked}>
            <STTypo fontFamily='Noto Sans' maxlength={40}>
              {t(allLabelKey)}
            </STTypo>
          </Checkbox>
        )}
        <VerticalCheckboxGroup value={checkedList} onChange={handleGroupChange}>
          {options.map(option => (
            <Checkbox key={option.value} value={option.value}>
              <STTypo fontFamily='Noto Sans' maxlength={15}>
                {translatable ? t(option.labelKey) : option.labelKey}
              </STTypo>
            </Checkbox>
          ))}
        </VerticalCheckboxGroup>
      </Container>
    </FilterSection>
  );
};

export default MultiSelectFilter;
